import { useCallback, useEffect, useMemo, useState } from "react";
import { useApolloClient } from "@apollo/client";
import { DataQuery, PageInfo } from "@models/ISchema";
import { programsQuery } from "@services/collections/programs/graphql/programs.query";
import getCurricularLabel from "../utils/getCurricularLabel";

interface UseGetProgramOptionsParams {
  scenario: string;
  campusId?: string;
  departmentId?: string;
  modalityId?: string;
  shiftId?: string;
  ignoreAccessFilters?: boolean;
  /**
   * Key to reset the search term and page
   */
  key?: string;
}

const useGetProgramOptions = ({
  scenario,
  campusId,
  departmentId,
  modalityId,
  shiftId,
  ignoreAccessFilters,
  key,
}: UseGetProgramOptionsParams) => {
  const client = useApolloClient();
  const [isLoading, setIsLoading] = useState(false);
  const [programPage, setProgramPage] = useState(1);
  const [termToSearch, setTermToSearch] = useState("");
  const [optionsProgram, setOptionsProgram] = useState(null);

  useEffect(() => {
    setTermToSearch("");
    setProgramPage(1);
  }, [key]);

  const variablesProgram = useMemo(() => {
    const hasSomeFilter = !!campusId || !!departmentId || !!modalityId || !!shiftId;

    const filters = {
      campusId: campusId ? { campusId: { eq: campusId } } : undefined,
      departmentId: departmentId ? { departmentId: { eq: departmentId } } : undefined,
      modalityId: modalityId ? { modalityId: { eq: modalityId } } : undefined,
      shiftId: shiftId ? { shiftId: { eq: shiftId } } : undefined,
    };

    return hasSomeFilter
      ? {
          fields: {
            and: Object.values(filters).filter(filter => !!filter),
          },
        }
      : {};
  }, [scenario, campusId, departmentId, modalityId, shiftId]);

  const getPrograms = useCallback(
    async (searchTerm = "", page = 1) => {
      const optionsPerPage = 10;
      setIsLoading(true);

      try {
        const variables = {
          query: programsQuery,
          variables: {
            scenarioId: scenario,
            filter: {
              ...variablesProgram,
              groupByCode: true,
              ...(ignoreAccessFilters !== undefined && { ignoreAccessFilters }),
              pagination: {
                page,
                size: optionsPerPage,
                searchTerm,
              },
            },
          },
        };

        const { data } = await client.query(variables);
        const dataQuery: DataQuery = data?.data;
        const pageInfo: PageInfo = dataQuery?.programs?.pageInfo;
        const programs = dataQuery?.programs;

        const options = [];

        if (programs) {
          programs.items.forEach(program => {
            options.push({
              label: getCurricularLabel(program?.code, program?.name),
              value: program.code,
              self: program,
            });
          });
        }

        setOptionsProgram(options);

        return { pageInfo, options };
      } catch (error) {
        console.error(error);
        setIsLoading(false);
        return null;
      }
    },
    [client, variablesProgram, ignoreAccessFilters],
  );

  const loadProgramOptions = async (searchTerm: string) => {
    let searchPage = 1;

    if (termToSearch === searchTerm) {
      searchPage = programPage;
    } else {
      setTermToSearch(searchTerm);
    }
    const { pageInfo, options: programOptions } = await getPrograms(searchTerm, searchPage);
    setProgramPage(programPage + 1);
    return {
      options: programOptions,
      hasMore: pageInfo?.hasNextPage,
      additional: {
        page: programPage,
      },
    };
  };

  return {
    optionsProgram,
    isLoading,
    loadProgramOptions,
  };
};

export default useGetProgramOptions;
